import ChatBubbleIcon from '@mui/icons-material/ChatBubble'
import { Box, Button, Dialog, DialogContent, DialogTitle, TextField, Typography } from '@mui/material'
import { FC, ReactNode, useMemo, useState } from 'react'
import { Toolbar, useCreate, useGetList, useNotify, useTranslate } from 'react-admin'
import { Bericht } from '../types/apiRequests'

interface RequestReactionsDialogProps {
  actieverzoekId: string
  samenwerkingTitel?: string | null
  actieverzoekTitel?: string | null
  renderTrigger: (open: () => void) => ReactNode
}

const MAX_MESSAGE_LENGTH = 512

const RequestReactionsDialog: FC<RequestReactionsDialogProps> = ({
  actieverzoekId,
  samenwerkingTitel,
  actieverzoekTitel,
  renderTrigger,
}) => {
  const [open, setOpen] = useState(false)
  const [bericht, setBericht] = useState('')
  const translate = useTranslate()
  const notify = useNotify()
  const [create, { isLoading: isSaving }] = useCreate()
  const { data: berichten, isLoading, refetch } = useGetList<Bericht>(
    `actieverzoeken/${actieverzoekId}/berichten`,
    {
      filter: {},
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'creatieDatumTijd', order: 'DESC' },
    },
    { enabled: open }
  )

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    setBericht('')
  }

  const remainingChars = useMemo(() => MAX_MESSAGE_LENGTH - bericht.length, [bericht.length])

  const onSave = () => {
    const inhoud = bericht.trim()
    if (!inhoud) {
      return
    }

    void create(
      `actieverzoeken/${actieverzoekId}/berichten`,
      { data: { bericht: inhoud } },
      {
        onSuccess: () => {
          setBericht('')
          notify('ra.notification.created', { type: 'info' })
          void refetch()
        },
        onError: (createError: unknown) => {
          const apiError = createError as {
            message?: string
            body?: { detail?: string; message?: string }
          }
          notify(
            apiError?.body?.detail ||
            apiError?.body?.message ||
            apiError?.message ||
            'ra.notification.http_error',
            { type: 'warning' }
          )
        },
      }
    )
  }

  return (
    <>
      {renderTrigger(handleOpen)}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          <ChatBubbleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {translate('data.reactions')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 0.5 }}>{samenwerkingTitel}</Typography>
          <Typography variant="subtitle2" sx={{ mb: 3 }}>{actieverzoekTitel}</Typography>

          {isLoading ? (
            <Typography variant="body2">{translate('ra.message.loading')}</Typography>
          ) : (
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              {(berichten ?? []).map(item => (
                <div key={item.id} style={{ marginBottom: '16px' }}>
                  <Typography variant="caption" color="text.secondary">
                    {item.zenderNaam || '-'}{item.creatieDatumTijd ? ` - ${new Date(item.creatieDatumTijd).toLocaleString('nl-NL')}` : ''}
                  </Typography>
                  <Typography variant="body2">{item.inhoud || ''}</Typography>
                </div>
              ))}
            </Box>
          )}

          <TextField
            label={translate('data.postReaction')}
            value={bericht}
            onChange={e => setBericht(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color={remainingChars < 0 ? 'error' : 'text.secondary'}>
            {translate('data.charactersLeft', { count: Math.max(remainingChars, 0) })}
          </Typography>

          <Toolbar>
            <Button onClick={handleClose}>{translate('ra.action.cancel')}</Button>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={isSaving || !bericht.trim()}
            >
              {translate('ra.action.save')}
            </Button>
          </Toolbar>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default RequestReactionsDialog
